import { useCallback, useEffect, useRef, useState } from "react";
import { TakeoverCard } from "@/components/TakeoverCard";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera, History, ClipboardList, Map } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { NativeSelect, StatusBadge, OverdueBadge, DemoBadge } from "@/components/common";
import { SEV_CLS } from "@/components/CriticitaFormDialog";
import { OrderFormDialog } from "@/components/OrderFormDialog";
import { fmtDT } from "@/lib/format";

const ACTS = {
  evaluate: "Valuta", assign: "Assegna responsabile", start: "Prendi in gestione", propose: "Proponi risoluzione",
  to_verify: "Avvia verifica", close: "Verifica e chiudi", reject: "Respingi risoluzione", reopen: "Riapri", cancel: "Annulla segnalazione",
  measures: "Misure provvisorie", comment: "Nota",
};
const PRIMARY = ["evaluate", "start", "propose", "to_verify", "close"];
const NEED = ["propose", "reject", "reopen", "cancel", "comment"];

const ActDialog = ({ c, act, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [sev, setSev] = useState(c.severity);
  const [resp, setResp] = useState(c.responsabile_id || "");
  const [measures, setMeasures] = useState(c.provisional_measures || "");
  const [people, setPeople] = useState([]);
  useEffect(() => {
    if (act === "assign") api.get("/users", { params: { commessa_id: c.commessa_id } }).then((r) => setPeople(r.data.filter((u) => ["responsabile", "caposquadra", "ceo"].includes(u.role))));
  }, [act, c.commessa_id]);
  const go = async () => {
    try {
      const { data } = await api.post(`/criticita/${c.id}/action`, { action: act, note, severity: sev, responsabile_id: resp, provisional_measures: measures });
      toast.success(`${ACTS[act]}: registrato`);
      onDone(data);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid={`cr-action-dialog-${act}`}>
        <DialogHeader><DialogTitle>{ACTS[act]}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {act === "evaluate" && <NativeSelect className="w-full" testid="cr-action-severity" value={sev} onChange={setSev}>{["bassa", "media", "alta", "emergenza"].map((s) => <option key={s} value={s}>{s}</option>)}</NativeSelect>}
          {act === "assign" && <NativeSelect className="w-full" testid="cr-action-responsabile" value={resp} onChange={setResp}><option value="">Seleziona…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}</NativeSelect>}
          {act === "measures" ? <Textarea value={measures} onChange={(e) => setMeasures(e.target.value)} data-testid="cr-action-measures" />
            : <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={NEED.includes(act) ? "Motivazione obbligatoria" : "Nota (facoltativa)"} data-testid="cr-action-note" />}
        </div>
        <DialogFooter><Button disabled={NEED.includes(act) && note.trim().length < 3} onClick={go} className="rounded-xl font-bold" data-testid="cr-action-confirm">Conferma</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function CriticitaDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const [act, setAct] = useState(null);
  const [orderOpen, setOrderOpen] = useState(false);
  const [commesse, setCommesse] = useState([]);
  const ref = useRef();
  const load = useCallback(() => {
    api.get(`/criticita/${id}`).then((r) => setC(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id]);
  useEffect(() => {
    load();
    api.get("/commesse").then((r) => setCommesse(r.data));
  }, [load]);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/criticita/${id}/photos`, fd);
      setC(data);
      toast.success("Foto caricata");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const view = async (p) => {
    const r = await api.get(`/files/${p.id}`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  if (err) return <div className="card-soft p-8 text-center font-bold text-red-600" data-testid="criticita-error">{err}</div>;
  if (!c) return <p className="text-muted-foreground">Caricamento…</p>;
  const avail = Object.keys(ACTS).filter((a) => c.can[a]);
  return (
    <div data-testid="criticita-detail-page" className="fade-up">
      <Link to="/criticita" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground"><ArrowLeft className="h-4 w-4" />Criticità</Link>
      <div className="card-soft mb-5 p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm text-muted-foreground">{c.code}</span>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${SEV_CLS[c.severity]}`} data-testid="cr-severity">{c.severity}</span>
          <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-bold" data-testid="cr-status">{c.status_label}</span><DemoBadge show={c.is_demo} />
        </div>
        <h1 className="mt-2 text-2xl font-extrabold sm:text-3xl">{c.title}</h1>
        {c.description && <p className="mt-2 text-sm text-muted-foreground">{c.description}</p>}
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          <div><p className="text-xs font-semibold uppercase text-muted-foreground">Categoria</p><p className="font-semibold">{c.category_label}</p></div>
          <div><p className="text-xs font-semibold uppercase text-muted-foreground">Cimitero / zona</p><p className="font-semibold">{c.cimitero_name} · {c.area_label || "—"}</p></div>
          <div><p className="text-xs font-semibold uppercase text-muted-foreground">Segnalante</p><p className="font-semibold">{c.reported_by_name} · {fmtDT(c.created_at)}</p></div>
          <div><p className="text-xs font-semibold uppercase text-muted-foreground">Responsabile</p><p className="font-semibold">{c.responsabile_name || "—"}</p></div>
        </div>
        {c.provisional_measures && <p className="mt-4 rounded-xl bg-amber-50 p-3 text-sm"><b>Misure provvisorie:</b> {c.provisional_measures}</p>}
        {c.area_id && <Link to={`/mappe/${c.cimitero_id}`} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary" data-testid="cr-open-map"><Map className="h-4 w-4" />Apri sulla mappa</Link>}
      </div>
      <div className="mb-6 space-y-2" data-testid="cr-actions">
        <div className="grid gap-2 sm:flex sm:flex-wrap">
          {avail.filter((a) => PRIMARY.includes(a)).map((a) => <Button key={a} onClick={() => setAct(a)} className="min-h-[52px] rounded-xl px-6 text-base font-bold" data-testid={`cr-action-${a}`}>{ACTS[a]}</Button>)}
          {c.can.create_order && <Button variant="outline" onClick={() => setOrderOpen(true)} className="min-h-[52px] rounded-xl font-bold" data-testid="cr-create-order"><ClipboardList className="mr-1 h-4 w-4" />Crea ordine collegato</Button>}
        </div>
        <div className="flex flex-wrap gap-2">
          {avail.filter((a) => !PRIMARY.includes(a)).map((a) => <Button key={a} variant={["cancel", "reject"].includes(a) ? "destructive" : "outline"} onClick={() => setAct(a)} className="h-10 rounded-xl" data-testid={`cr-action-${a}`}>{ACTS[a]}</Button>)}
          {c.can.photo && <><input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="cr-photo-input" />
            <Button variant="outline" onClick={() => ref.current.click()} className="h-10 rounded-xl" data-testid="cr-photo-button"><Camera className="mr-1 h-4 w-4" />Aggiungi foto</Button></>}
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-[1fr_420px]">
        <div className="space-y-5">
          <TakeoverCard type="criticita" targetId={c.id} closed={["chiusa", "annullata"].includes(c.status)} />
          <section className="card-soft p-5" data-testid="cr-orders"><h3 className="mb-3 font-bold">Ordini collegati ({c.orders.length})</h3>
            {c.orders.length === 0 && <p className="text-sm text-muted-foreground">Nessun ordine collegato.</p>}
            {c.orders.map((o) => <Link key={o.id} to={`/ordini/${o.id}`} className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border p-3 text-sm"><span className="font-mono text-xs">{o.code}</span><span className="flex-1 font-semibold">{o.title}</span><StatusBadge status={o.status} /><OverdueBadge order={o} /></Link>)}
          </section>
          <section className="card-soft p-5" data-testid="cr-photos"><h3 className="mb-3 font-bold">Fotografie ({c.photos.length})</h3>
            <div className="flex flex-wrap gap-2">{c.photos.map((p) => <button key={p.id} onClick={() => view(p)} className="rounded-xl border px-3 py-2 text-xs">{p.filename} · {p.by_name}</button>)}</div>
          </section>
        </div>
        <section className="card-soft p-5" data-testid="cr-timeline"><h3 className="mb-3 flex items-center gap-2 font-bold"><History className="h-4 w-4 text-primary" />Storico</h3>
          <ol className="space-y-3 border-l-2 border-accent pl-4">{c.events.slice().reverse().map((e) => (
            <li key={e.id}><p className="text-sm font-semibold capitalize">{e.type.replaceAll("_", " ")}{e.to_status && e.from_status ? ` → ${e.to_status.replaceAll("_", " ")}` : ""}</p>{e.note && <p className="text-sm text-muted-foreground">{e.note}</p>}<p className="text-xs text-muted-foreground">{e.actor_name} · {fmtDT(e.at)}</p></li>
          ))}</ol>
        </section>
      </div>
      {act && <ActDialog c={c} act={act} onClose={() => setAct(null)} onDone={setC} />}
      <OrderFormDialog open={orderOpen} onOpenChange={setOrderOpen} commesse={commesse} onCreated={load}
        preset={{ commessa_id: c.commessa_id, cimitero_id: c.cimitero_id, area_id: c.area_id, area: c.area_label || "", criticita_id: c.id, title: `Intervento ${c.code}: ${c.title}`.slice(0, 200), priority: c.severity === "emergenza" ? "urgente" : c.severity === "bassa" ? "bassa" : c.severity }} />
    </div>
  );
}
